import type { QueryClient } from "@tanstack/react-query";
import type { AssetScope } from "../components/gallery/galleryTree";
import {
  listAssetsApiAssetsGetQueryKey,
  listProjectAssetsApiProjectsProjectIdAssetsGetQueryKey,
} from "../generated/api/@tanstack/react-query.gen";
import {
  attachProjectAssetApiProjectsProjectIdAssetsPost,
  createAssetApiAssetsPost,
  createImageAssetApiAssetsImagePost,
  saveImageAsAssetApiImagesImageIdSaveAsAssetPost,
  shareAssetWithOrgApiAssetsAssetIdShareWithOrgPost,
} from "../generated/api/sdk.gen";
import type {
  AssetResponse,
  CreateAssetRequest,
} from "../generated/api/types.gen";

/** Carries the asset a save already created between attempts, so a retry after
 *  a failed project attach re-files that asset instead of creating a second
 *  copy. Hold one per save (a `useRef`), and clear it whenever the inputs the
 *  asset was created from change. */
export type CreatedAssetBox = { current: AssetResponse | null };

/** Org-scoped assets are created personal first, then promoted to the org. */
async function promoteAssetToOrg(asset: AssetResponse): Promise<AssetResponse> {
  const { data: shared } =
    await shareAssetWithOrgApiAssetsAssetIdShareWithOrgPost({
      path: { asset_id: asset.id },
    });
  if (!shared) throw new Error("Failed to share asset with organization");
  return shared;
}

export async function createColorAssetForScope(
  body: CreateAssetRequest,
  scope: AssetScope,
): Promise<AssetResponse> {
  const { data: created } = await createAssetApiAssetsPost({ body });
  if (!created) throw new Error("Failed to create asset");
  return scope === "organization" ? promoteAssetToOrg(created) : created;
}

export async function createImageAssetForScope(
  file: File,
  name: string,
  scope: AssetScope,
): Promise<AssetResponse> {
  const { data: created } = await createImageAssetApiAssetsImagePost({
    body: { file, name },
  });
  if (!created) throw new Error("Failed to create asset");
  return scope === "organization" ? promoteAssetToOrg(created) : created;
}

/** Create a personal image asset, attach it to the project when given, and
 *  invalidate the asset lists so open pickers refresh. */
export async function createImageAssetForProject(
  queryClient: QueryClient,
  {
    file,
    name,
    projectId,
    created,
  }: {
    file: File;
    name: string;
    projectId?: string | null;
    created?: CreatedAssetBox;
  },
): Promise<AssetResponse> {
  return createAndFile(
    queryClient,
    async () => {
      const { data } = await createImageAssetApiAssetsImagePost({
        body: { file, name },
      });
      if (!data) throw new Error("Failed to create asset");
      return data;
    },
    { projectId, created },
  );
}

/** Save the design image behind `imageId` into the library, without making the
 *  browser download and re-upload it. */
export async function saveImageAsAsset(
  queryClient: QueryClient,
  {
    imageId,
    name,
    projectId,
    created,
  }: {
    imageId: string;
    name: string;
    projectId?: string | null;
    created?: CreatedAssetBox;
  },
): Promise<AssetResponse> {
  return createAndFile(
    queryClient,
    async () => {
      const { data } = await saveImageAsAssetApiImagesImageIdSaveAsAssetPost({
        path: { image_id: imageId },
        body: { name },
      });
      if (!data) throw new Error("Failed to save image as asset");
      return data;
    },
    { projectId, created },
  );
}

/** Create the asset, attach it to its project when it has one, then invalidate
 *  the asset lists so open pickers and the gallery refresh.
 *
 *  The two calls are not atomic: once the asset exists it is in the library
 *  whether or not the attach lands, so the library list is invalidated either
 *  way, and `created` lets a retry skip creation and re-run the attach alone
 *  (attaching is idempotent server-side). A name edited between a partial
 *  failure and the retry does not rename the asset that already exists. */
async function createAndFile(
  queryClient: QueryClient,
  create: () => Promise<AssetResponse>,
  {
    projectId,
    created,
  }: { projectId?: string | null; created?: CreatedAssetBox },
): Promise<AssetResponse> {
  const asset = created?.current ?? (await create());
  if (created) created.current = asset;

  try {
    if (projectId) {
      await attachProjectAssetApiProjectsProjectIdAssetsPost({
        path: { project_id: projectId },
        body: { asset_id: asset.id },
      });
      queryClient.invalidateQueries({
        queryKey: listProjectAssetsApiProjectsProjectIdAssetsGetQueryKey({
          path: { project_id: projectId },
        }),
      });
    }
  } finally {
    queryClient.invalidateQueries({
      queryKey: listAssetsApiAssetsGetQueryKey(),
    });
  }
  return asset;
}
